import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import BOMUpdateModal from './BOMUpdateModal';
import './WorkOrderDetail.css';

function WorkOrderDetail() {
  const { workOrderNumber } = useParams();
  const navigate = useNavigate();
  const [workOrder, setWorkOrder] = useState(null);
  const [productionStatus, setProductionStatus] = useState(null);
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bomData, setBomData] = useState(null);
  const [checkingBOM, setCheckingBOM] = useState(false);
  const [showBOMModal, setShowBOMModal] = useState(false);

  useEffect(() => {
    loadWorkOrderData();
  }, [workOrderNumber]);

  const loadWorkOrderData = async () => {
    try {
      setLoading(true);

      // Fetch the specific work order using the detail endpoint
      const workOrderResponse = await axios.get(`/api/workorders/${workOrderNumber}`);
      const wo = workOrderResponse.data;

      if (!wo) {
        throw new Error('Work order not found');
      }

      setWorkOrder(wo);

      // Fetch production status from batch_work_orders
      try {
        const statusResponse = await axios.get(`/api/workorders/${workOrderNumber}/production-status`);
        setProductionStatus(statusResponse.data);
      } catch (err) {
        console.log('No production data yet for this work order');
      }

      // Fetch associated carts
      try {
        const cartsResponse = await axios.get(`/api/workorders/${workOrderNumber}/carts`);
        setCarts(cartsResponse.data);
      } catch (err) {
        console.log('No carts yet for this work order');
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const calculateProgress = () => {
    if (!productionStatus) return 0;
    const total = productionStatus.total_units || 0;
    const built = productionStatus.units_built || 0;
    return total > 0 ? (built / total) * 100 : 0;
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: '#999',
      in_use: '#2196F3',
      completed: '#4CAF50'
    };
    return colors[status] || '#757575';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const checkBOM = async () => {
    if (!workOrder?.id) return;

    setCheckingBOM(true);
    try {
      const response = await axios.get(`/api/workorders/${workOrder.id}/bom-check`);
      setBomData(response.data);

      // Auto-show modal if there are changes
      if (response.data.hasChanges) {
        setShowBOMModal(true);
      }
    } catch (err) {
      console.error('Error checking BOM:', err);
    } finally {
      setCheckingBOM(false);
    }
  };

  const handleBOMUpdate = (result) => {
    console.log('BOM updated:', result);
    setBomData(null);
    // Reload work order data to reflect changes
    loadWorkOrderData();
  };

  if (loading) {
    return (
      <div className="work-order-detail loading">
        <div className="spinner"></div>
        <p>Loading work order details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="work-order-detail error">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/workorders')}>Back to Work Orders</button>
      </div>
    );
  }

  const progress = calculateProgress();

  return (
    <div className="work-order-detail">
      {/* Header */}
      <div className="detail-header">
        <button className="back-button" onClick={() => navigate('/workorders')}>
          ← Back to Work Orders
        </button>
        <h1>Work Order #{workOrderNumber}</h1>
        <div className="header-actions">
          <button
            className="check-bom-button"
            onClick={checkBOM}
            disabled={checkingBOM}
          >
            {checkingBOM ? 'Checking...' : 'Check BOM'}
          </button>
          <button className="refresh-button" onClick={loadWorkOrderData}>
            Refresh
          </button>
        </div>
      </div>

      {/* Work Order Info */}
      <div className="detail-section">
        <h2>Order Information</h2>
        <div className="info-grid">
          <div className="info-item">
            <label>Customer</label>
            <span>{workOrder.customerName}</span>
          </div>
          <div className="info-item">
            <label>Item</label>
            <span>{workOrder.itemDescription}</span>
          </div>
          <div className="info-item">
            <label>SKU</label>
            <span>{workOrder.itemSku}</span>
          </div>
          <div className="info-item">
            <label>Order Date</label>
            <span>{formatDate(workOrder.orderDate)}</span>
          </div>
          <div className="info-item">
            <label>Ship Date</label>
            <span>{formatDate(workOrder.scheduledEndDate || workOrder.dateRequired)}</span>
          </div>
          <div className="info-item">
            <label>Quantity</label>
            <span>{workOrder.quantity} units</span>
          </div>
          <div className="info-item">
            <label>Quantity Built</label>
            <span>{workOrder.quantityCompleted || workOrder.quantityBuilt || 0} units</span>
          </div>
          <div className="info-item">
            <label>Quantity Remaining</label>
            <span>{(workOrder.quantity || 0) - (workOrder.quantityCompleted || workOrder.quantityBuilt || 0)} units</span>
          </div>
          <div className="info-item">
            <label>Case Qty</label>
            <span>{workOrder.caseQty || 1} per case</span>
          </div>
          <div className="info-item">
            <label>Total Cases</label>
            <span>{Math.ceil(workOrder.quantity / (workOrder.caseQty || 1))} cases</span>
          </div>
        </div>
      </div>

      {/* Production Progress */}
      <div className="detail-section">
        <h2>Production Progress</h2>

        {productionStatus ? (
          <>
            <div className="overall-progress">
              <div className="progress-header">
                <span>Overall Completion</span>
                <span className="progress-text">
                  {productionStatus.units_built || 0} / {productionStatus.total_units || 0} units
                </span>
              </div>
              <div className="progress-bar-large">
                <div
                  className="progress-fill"
                  style={{
                    width: `${progress}%`,
                    background: progress === 100 ? '#4CAF50' : '#2196F3'
                  }}
                >
                  {progress.toFixed(1)}%
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="no-production">
            <p>No production data available yet.</p>
            <p>This work order may not have been added to a batch.</p>
          </div>
        )}
      </div>

      {/* Associated Carts */}
      {carts.length > 0 && (
        <div className="detail-section">
          <h2>Associated Carts</h2>
          <div className="carts-table">
            <table>
              <thead>
                <tr>
                  <th>Cart #</th>
                  <th>Barcode</th>
                  <th>Status</th>
                  <th>Units</th>
                  <th>Location</th>
                  <th>Last Scan</th>
                </tr>
              </thead>
              <tbody>
                {carts.map(cart => (
                  <tr key={cart.id}>
                    <td>{cart.cart_number}</td>
                    <td className="barcode">{cart.barcode}</td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ background: getStatusColor(cart.status) }}
                      >
                        {cart.status}
                      </span>
                    </td>
                    <td>{cart.units || 0}</td>
                    <td>{cart.current_location || 'N/A'}</td>
                    <td>{cart.last_scanned_at ? new Date(cart.last_scanned_at).toLocaleString() : 'Never'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Additional Details */}
      <div className="detail-section">
        <h2>Additional Information</h2>
        <div className="info-grid">
          <div className="info-item">
            <label>Fragrance</label>
            <span>{workOrder.fragranceName || workOrder.fragranceOilName || 'N/A'}</span>
          </div>
          <div className="info-item">
            <label>Vessel</label>
            <span>{workOrder.vesselName || 'N/A'}</span>
          </div>
          <div className="info-item">
            <label>Cart Quantity</label>
            <span>{workOrder.cartQuantity || 'N/A'}</span>
          </div>
          <div className="info-item">
            <label>Status</label>
            <span className="wo-status">{workOrder.status || 'Pending'}</span>
          </div>
        </div>
      </div>

      {/* BOM Update Modal */}
      {showBOMModal && bomData && (
        <BOMUpdateModal
          bomData={bomData}
          onClose={() => setShowBOMModal(false)}
          onUpdate={handleBOMUpdate}
        />
      )}
    </div>
  );
}

export default WorkOrderDetail;